import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, Clock, Pill, Download, ArrowLeft, Sparkles, Share2, BookOpen, Copy, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { exportRecommendationPDF } from "@/lib/pdf";
import { useAuth } from "@/context/AuthContext";
import { detectRegion, openAffiliate } from "@/lib/affiliate";

export default function Recommendations() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [region] = useState(detectRegion());
  const [linksByKey, setLinksByKey] = useState({});

  useEffect(() => {
    api
      .get(`/recommendations/${id}`)
      .then((r) => setData(r.data))
      .catch(() => {
        toast.error("Could not load recommendation");
        nav("/dashboard");
      })
      .finally(() => setLoading(false));
  }, [id, nav]);

  // Lazy-fetch the primary retailer link for each supplement once data loads
  useEffect(() => {
    if (!data) return;
    const fetches = data.supplements.map((s) =>
      api
        .get(`/supplements/${s.key}`, { params: { region } })
        .then((r) => [s.key, r.data.affiliate_links?.[0] || null])
        .catch(() => [s.key, null])
    );
    Promise.all(fetches).then((pairs) => setLinksByKey(Object.fromEntries(pairs)));
  }, [data, region]);

  const onShare = async () => {
    setSharing(true);
    try {
      const { data: s } = await api.post(`/recommendations/${id}/share`);
      const url = `${window.location.origin}/share/${s.token}`;
      setShareUrl(url);
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied to clipboard");
      } catch {
        toast.success("Share link created");
      }
    } catch {
      toast.error("Could not create share link");
    } finally {
      setSharing(false);
    }
  };

  const onPDF = () => {
    try {
      exportRecommendationPDF(data, { ownerName: user?.name || "" });
    } catch (e) {
      toast.error("Could not export PDF");
    }
  };

  if (loading) {
    return (
      <main className="max-w-4xl mx-auto px-6 sm:px-8 py-20 text-center">
        <p className="label-eyebrow">Loading</p>
        <p className="font-serif text-3xl mt-2">Preparing your plan…</p>
      </main>
    );
  }
  if (!data) return null;

  return (
    <main className="max-w-4xl mx-auto px-6 sm:px-8 py-10 sm:py-16" data-testid="recommendations-page">
      <div className="flex items-center justify-between mb-10 flex-wrap gap-3">
        <Link to="/dashboard" className="btn-ghost text-sm flex items-center gap-1.5" data-testid="rec-back">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> History
        </Link>
        <div className="flex flex-wrap gap-2">
          <button onClick={onShare} className="btn-ghost text-sm flex items-center gap-1.5" disabled={sharing} data-testid="rec-share">
            <Share2 className="w-4 h-4" strokeWidth={1.5} /> {sharing ? "Creating…" : "Share with practitioner"}
          </button>
          <button onClick={onPDF} className="btn-ghost text-sm flex items-center gap-1.5" data-testid="rec-pdf">
            <Download className="w-4 h-4" strokeWidth={1.5} /> Download PDF
          </button>
        </div>
      </div>

      {shareUrl && (
        <div
          className="card-soft p-4 mb-8 flex flex-wrap items-center gap-3"
          style={{ background: "var(--bg-2)" }}
          data-testid="share-url-display"
        >
          <Share2 className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--brand)" }} />
          <p className="text-sm flex-1 break-all" style={{ color: "var(--text)" }}>{shareUrl}</p>
          <button
            className="btn-ghost text-xs flex items-center gap-1"
            onClick={() => {
              navigator.clipboard.writeText(shareUrl);
              toast.success("Copied");
            }}
            data-testid="share-copy"
          >
            <Copy className="w-3 h-3" strokeWidth={1.5} /> Copy
          </button>
        </div>
      )}

      <header className="mb-10 fade-up">
        <p className="label-eyebrow mb-3">Your personalised plan</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight">
          {data.supplements.length} supplements, thoughtfully chosen for you.
        </h1>
        <p className="mt-3 text-sm" style={{ color: "var(--text-2)" }}>
          Created {new Date(data.created_at).toLocaleDateString(undefined, { dateStyle: "long" })}
        </p>
      </header>

      {data.ai_explanation && (
        <section className="card-soft p-7 sm:p-9 mb-10 fade-up" style={{ background: "var(--bg-2)" }} data-testid="ai-explanation">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
            <p className="label-eyebrow" style={{ color: "var(--accent)" }}>A note for you</p>
          </div>
          <div className="font-serif text-lg sm:text-xl leading-relaxed whitespace-pre-line" style={{ color: "var(--text)" }}>
            {data.ai_explanation}
          </div>
        </section>
      )}

      <div className="space-y-5">
        {data.supplements.map((s, i) => (
          <article
            key={s.key}
            className="card-soft p-6 sm:p-8 fade-up"
            style={{ animationDelay: `${i * 60}ms` }}
            data-testid={`supplement-card-${s.key}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <p className="label-eyebrow mb-1.5">{s.category}</p>
                <Link
                  to={`/supplements/${s.key}`}
                  className="font-serif text-2xl sm:text-3xl leading-tight hover:underline"
                  data-testid={`supplement-link-${s.key}`}
                >
                  {s.name}
                </Link>
              </div>
              <div className="flex flex-wrap gap-2">
                {s.addresses.map((a) => (
                  <span
                    key={a}
                    className="text-xs px-3 py-1 rounded-full"
                    style={{ background: "var(--bg-2)", color: "var(--text-2)", border: "1px solid var(--border)" }}
                  >
                    {a}
                  </span>
                ))}
              </div>
            </div>

            <p className="leading-relaxed mb-5" style={{ color: "var(--text-2)" }}>{s.benefits}</p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-4 rounded-2xl" style={{ background: "var(--bg-2)" }}>
                <Pill className="w-4 h-4 mt-0.5" strokeWidth={1.5} style={{ color: "var(--brand)" }} />
                <div>
                  <p className="label-eyebrow mb-1">Dosage</p>
                  <p className="text-sm">{s.dosage}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-2xl" style={{ background: "var(--bg-2)" }}>
                <Clock className="w-4 h-4 mt-0.5" strokeWidth={1.5} style={{ color: "var(--brand)" }} />
                <div>
                  <p className="label-eyebrow mb-1">Timing</p>
                  <p className="text-sm">{s.timing}</p>
                </div>
              </div>
            </div>

            <div className="mt-5 p-4 rounded-2xl" style={{ background: "#FBF4F0", border: "1px solid #ECDCD3" }}>
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 mt-0.5" strokeWidth={1.5} style={{ color: "var(--warning)" }} />
                <div className="space-y-2">
                  <div>
                    <p className="label-eyebrow mb-1" style={{ color: "var(--warning)" }}>Warnings</p>
                    <p className="text-sm">{s.warnings}</p>
                  </div>
                  <div>
                    <p className="label-eyebrow mb-1" style={{ color: "var(--warning)" }}>Interactions</p>
                    <p className="text-sm">{s.interactions}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <Link
                to={`/supplements/${s.key}`}
                className="text-xs inline-flex items-center gap-1 hover:underline"
                style={{ color: "var(--brand)" }}
                data-testid={`supplement-research-${s.key}`}
              >
                <BookOpen className="w-3 h-3" strokeWidth={1.5} /> View research & citations
              </Link>
              {linksByKey[s.key] && (
                <button
                  onClick={() =>
                    openAffiliate({
                      supplement_key: s.key,
                      retailer: linksByKey[s.key].retailer,
                      region,
                      recommendation_id: data.id,
                      url: linksByKey[s.key].url,
                    })
                  }
                  className="btn-ghost text-xs flex items-center gap-1.5"
                  data-testid={`buy-cta-${s.key}`}
                >
                  <ShoppingBag className="w-3 h-3" strokeWidth={1.5} />
                  Buy on {linksByKey[s.key].label}
                </button>
              )}
            </div>
          </article>
        ))}
      </div>

      <section className="mt-12 p-6 rounded-2xl text-sm" style={{ background: "var(--bg-2)", color: "var(--text-2)" }}>
        <p>
          <strong style={{ color: "var(--text)" }}>Important: </strong>
          This information is educational only and is not a substitute for personalised medical advice.
          Please discuss with a qualified healthcare professional, especially if you take medications,
          are pregnant or nursing, or have a chronic condition.
        </p>
      </section>

      <div className="mt-10 flex flex-wrap gap-3">
        <Link to="/tracker" className="btn-primary" data-testid="rec-go-tracker">
          Start tracking my supplements
        </Link>
        <Link to="/questionnaire" className="btn-ghost text-sm" data-testid="rec-retake">
          Take a new assessment
        </Link>
      </div>
    </main>
  );
}
